"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/app/_module/lib/utils";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { Label } from "@/app/_module/components/ui/label";
import { Switch } from "@/app/_module/components/ui/switch";
import { Slider } from "@/app/_module/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import { useToast } from "@/app/_module/hooks/use-toast";
import { oauthConnectionService } from "@/app/_module/services/oauthConnectionService";
import type { EmailOAuthConnection } from "@/app/_module/types/email-oauth";
import type {
  CalendarSyncPreferences,
  SyncConflict,
  CalendarConnectionStatus,
} from "@/app/(authenticated)/appointments/_modules/types/calendar-sync";
import {
  Mail,
  Calendar,
  Check,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
  Unplug,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { isNativePlatform } from "@/lib/capacitor/platform";

// ── Animation variants ────────────────────────────────────────
const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE },
  },
};

// ── Google logo SVG ───────────────────────────────────────────
const GoogleLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// ── Microsoft logo SVG ────────────────────────────────────────
const MicrosoftLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#F35325" d="M1 1h10v10H1z"/>
    <path fill="#81BC06" d="M13 1h10v10H13z"/>
    <path fill="#05A6F0" d="M1 13h10v10H1z"/>
    <path fill="#FFBA08" d="M13 13h10v10H13z"/>
  </svg>
);

export function OAuthAccountConnection() {
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<"google" | "microsoft" | null>(null);
  const [disconnecting, setDisconnecting] = useState<"google" | "microsoft" | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);

  const [googleConnection, setGoogleConnection] = useState<EmailOAuthConnection | null>(null);
  const [microsoftConnection, setMicrosoftConnection] = useState<EmailOAuthConnection | null>(null);
  const [calendarPreferences, setCalendarPreferences] = useState<CalendarSyncPreferences | null>(null);
  const [calendarStatus, setCalendarStatus] = useState<CalendarConnectionStatus | null>(null);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);

  // Token health and calendar selection states
  const [googleHealth, setGoogleHealth] = useState<"healthy" | "expired" | "error" | null>(null);
  const [microsoftHealth, setMicrosoftHealth] = useState<"healthy" | "expired" | "error" | null>(null);
  const [showCalendarSelect, setShowCalendarSelect] = useState<"google" | "microsoft" | null>(null);
  const [calendars, setCalendars] = useState<Array<{ id: string; name: string; primary: boolean }>>([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);

  // Handle OAuth callback result from URL params
  useEffect(() => {
    const oauthResult = searchParams.get("oauth");
    const provider = searchParams.get("provider");
    const errorMessage = searchParams.get("errorMessage");

    if (oauthResult) {
      if (oauthResult === "success") {
        toast({
          title: "Account Connected",
          description: `Your ${provider === "google" ? "Google" : provider === "microsoft" ? "Microsoft" : ""} account has been connected successfully.`,
        });
      } else if (oauthResult === "error") {
        const decodedError = errorMessage ? decodeURIComponent(errorMessage) : null;
        toast({
          title: "Connection Failed",
          description: decodedError || "Failed to connect your account. Please try again.",
          variant: "destructive",
          duration: 10000,
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.delete("oauth");
      url.searchParams.delete("provider");
      url.searchParams.delete("errorMessage");
      router.replace(url.pathname + url.search, { scroll: false });
    }
  }, [searchParams, toast, router]);

  // Load connection status on mount
  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    setLoading(true);
    try {
      const [status, preferences, calStatus, conflictsList] = await Promise.all([
        oauthConnectionService.getConnectionStatus(),
        oauthConnectionService.getCalendarPreferences(),
        oauthConnectionService.getCalendarConnections(),
        oauthConnectionService.getSyncConflicts(),
      ]);

      if (status.google) setGoogleConnection(status.google);
      if (status.microsoft) setMicrosoftConnection(status.microsoft);
      if (preferences) setCalendarPreferences(preferences);
      if (calStatus) setCalendarStatus(calStatus);
      setConflicts(conflictsList);

      if (status.google) {
        const health = await oauthConnectionService.checkTokenHealth("google");
        setGoogleHealth(health.status);
      }
      if (status.microsoft) {
        const health = await oauthConnectionService.checkTokenHealth("microsoft");
        setMicrosoftHealth(health.status);
      }

      if (preferences && !preferences.timezone) {
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (detectedTimezone) {
          await oauthConnectionService.updateCalendarPreferences({
            timezone: detectedTimezone,
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load connection status",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Native connect flow: consent runs in the system browser sheet while the
  // app keeps running underneath. The sheet's redirect page can't message us
  // (separate browser, no shared session), so poll until the backend has
  // stored the tokens, then close the sheet and refresh. Also bails when the
  // user dismisses the sheet themselves.
  const waitForNativeConnection = async (provider: "google" | "microsoft") => {
    const deadline = Date.now() + 3 * 60 * 1000;
    let sheetClosed = false;
    const { Browser } = await import("@capacitor/browser");
    const sub = await Browser.addListener("browserFinished", () => {
      sheetClosed = true;
    });
    try {
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const status = await oauthConnectionService.getConnectionStatus();
          const conn = provider === "google" ? status.google : status.microsoft;
          if (conn?.status === "connected") {
            // Android Custom Tabs can't be closed programmatically — the
            // catch leaves the user to swipe it away, already connected.
            await Browser.close().catch(() => {});
            toast({
              title: "Account Connected",
              description: `Your ${provider === "google" ? "Google" : "Microsoft"} account has been connected successfully.`,
            });
            await loadConnectionStatus();
            return;
          }
        } catch {
          /* transient — keep polling */
        }
        if (sheetClosed) break;
      }
      // Sheet dismissed or timed out without a confirmed connect — show
      // whatever state actually exists.
      await loadConnectionStatus();
    } finally {
      sub.remove();
    }
  };

  const handleConnectGoogle = async () => {
    // DEMO SANDBOX: OAuth would hand the sandbox a REAL mailbox.
    //
    // This is the one gate here that is about the visitor's own safety rather than our
    // costs: the flow redirects to Google/Microsoft and, on consent, stores live tokens
    // against a POOLED seat — so a stranger's inbox and calendar would be readable by
    // whoever claims that seat next, and the reset would not revoke the grant. Blocked
    // before the redirect, so no consent screen is ever shown.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Connecting an email account is disabled in the demo sandbox. Start a free trial to sync your real inbox and calendar.",
        }),
      );
      return;
    }


    setConnecting("google");
    try {
      await oauthConnectionService.connectGoogle();
      // Web navigates away to Google here; native stays on this screen with
      // the consent sheet on top — wait it out, then clear the spinner.
      if (isNativePlatform()) {
        await waitForNativeConnection("google");
        setConnecting(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to connect Google account",
        variant: "destructive",
      });
      setConnecting(null);
    }
  };

  const handleConnectMicrosoft = async () => {
    // DEMO SANDBOX: OAuth would hand the sandbox a REAL mailbox.
    //
    // This is the one gate here that is about the visitor's own safety rather than our
    // costs: the flow redirects to Google/Microsoft and, on consent, stores live tokens
    // against a POOLED seat — so a stranger's inbox and calendar would be readable by
    // whoever claims that seat next, and the reset would not revoke the grant. Blocked
    // before the redirect, so no consent screen is ever shown.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Connecting an email account is disabled in the demo sandbox. Start a free trial to sync your real inbox and calendar.",
        }),
      );
      return;
    }


    setConnecting("microsoft");
    try {
      await oauthConnectionService.connectMicrosoft();
      if (isNativePlatform()) {
        await waitForNativeConnection("microsoft");
        setConnecting(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to connect Microsoft account",
        variant: "destructive",
      });
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider: "google" | "microsoft") => {
    setDisconnecting(provider);
    try {
      const result = await oauthConnectionService.disconnectProvider(provider);

      if (result.success) {
        if (provider === "google") {
          setGoogleConnection(null);
        } else {
          setMicrosoftConnection(null);
        }

        toast({
          title: "Disconnected",
          description: `${provider === "google" ? "Google" : "Microsoft"} account disconnected successfully`,
        });

        await loadConnectionStatus();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to disconnect ${provider === "google" ? "Google" : "Microsoft"} account`,
        variant: "destructive",
      });
    } finally {
      setDisconnecting(null);
    }
  };

  const handleUpdateSyncDirection = async (direction: "one_way" | "bidirectional") => {
    try {
      const result = await oauthConnectionService.updateCalendarPreferences({
        syncDirection: direction,
      });

      if (result.success && result.preferences) {
        setCalendarPreferences(result.preferences);
        toast({
          title: "Sync Direction Updated",
          description: `Calendar sync direction set to ${direction === "one_way" ? "one-way" : "bidirectional"}`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update sync direction",
        variant: "destructive",
      });
    }
  };

  const handleUpdateConflictResolution = async (
    strategy: "last_write_wins" | "app_priority" | "calendar_priority" | "manual"
  ) => {
    try {
      const result = await oauthConnectionService.updateCalendarPreferences({
        conflictResolution: strategy,
      });

      if (result.success && result.preferences) {
        setCalendarPreferences(result.preferences);
        toast({
          title: "Conflict Resolution Updated",
          description: "Conflict resolution strategy updated successfully",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update conflict resolution strategy",
        variant: "destructive",
      });
    }
  };

  const handleToggleAutoSync = useCallback(async (enabled: boolean) => {
    setSaving(true);
    try {
      const result = await oauthConnectionService.updateCalendarPreferences({
        autoSync: enabled,
      });

      if (result.success && result.preferences) {
        setCalendarPreferences(result.preferences);
        toast({
          title: "Auto-Sync Updated",
          description: `Auto-sync ${enabled ? "enabled" : "disabled"}`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update auto-sync setting",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [toast]);

  // Calendar selection handlers
  const handleOpenCalendarSelect = async (provider: "google" | "microsoft") => {
    setShowCalendarSelect(provider);
    setLoadingCalendars(true);
    setCalendars([]);
    setSelectedCalendarId(null);

    try {
      const result = await oauthConnectionService.getCalendarList(provider);
      if (result.calendars.length > 0) {
        setCalendars(result.calendars);
        const primary = result.calendars.find((c) => c.primary);
        if (primary) {
          setSelectedCalendarId(primary.id);
        }
      } else if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
        setShowCalendarSelect(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch calendar list",
        variant: "destructive",
      });
      setShowCalendarSelect(null);
    } finally {
      setLoadingCalendars(false);
    }
  };

  const handleSelectCalendar = async () => {
    if (!showCalendarSelect || !selectedCalendarId) return;

    const selectedCal = calendars.find((c) => c.id === selectedCalendarId);
    const result = await oauthConnectionService.selectCalendar(
      showCalendarSelect,
      selectedCalendarId,
      selectedCal?.name
    );

    if (result.success) {
      toast({
        title: "Calendar Selected",
        description: `Calendar "${selectedCal?.name || selectedCalendarId}" will be used for sync`,
      });
      setShowCalendarSelect(null);
      await loadConnectionStatus();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to select calendar",
        variant: "destructive",
      });
    }
  };

  const handleUpdateSyncInterval = async (minutes: number) => {
    setSaving(true);
    try {
      const result = await oauthConnectionService.updateCalendarPreferences({
        syncInterval: minutes,
      });

      if (result.success && result.preferences) {
        setCalendarPreferences(result.preferences);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update sync interval",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const result = await oauthConnectionService.syncAllAppointments();

      if (result.success) {
        toast({
          title: "Sync Complete",
          description: `Synced ${result.synced} appointments${result.failed > 0 ? `, ${result.failed} failed` : ""}`,
        });
        await loadConnectionStatus();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: "Failed to sync appointments to calendar",
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleResolveConflict = async (
    conflictId: string,
    resolution: "app" | "calendar"
  ) => {
    setResolving(conflictId);
    try {
      const result = await oauthConnectionService.resolveSyncConflict(conflictId, {
        conflictId,
        resolution: resolution === "app" ? "app_priority" : "calendar_priority",
      });

      if (result.success) {
        toast({
          title: "Conflict Resolved",
          description: `Conflict resolved using ${resolution} version`,
        });
        setConflicts(conflicts.filter((c) => c.id !== conflictId));
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resolve conflict",
        variant: "destructive",
      });
    } finally {
      setResolving(null);
    }
  };

  const formatSyncInterval = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    return hours === 1 ? "1 hour" : `${hours} hours`;
  };

  const getSyncIntervalValue = (minutes: number): number => {
    const intervals = [15, 30, 60, 120, 240];
    return intervals.indexOf(minutes);
  };

  const getMinutesFromSliderValue = (value: number): number => {
    const intervals = [15, 30, 60, 120, 240];
    return intervals[value] || 30;
  };

  // ── Provider connection panel ───────────────────────────────
  const renderProviderPanel = (
    connection: EmailOAuthConnection | null,
    provider: "google" | "microsoft"
  ) => {
    const providerName = provider === "google" ? "Google" : "Microsoft";
    const isConnected = connection?.status === "connected";
    const health = provider === "google" ? googleHealth : microsoftHealth;
    const isExpired = isConnected && health === "expired";

    return (
      <motion.div variants={fadeUp}>
        <div
          className={cn(
            "relative rounded-xl border transition-all duration-300",
            "hover:shadow-md hover:shadow-black/[0.03]",
            isConnected && !isExpired
              ? "border-emerald-200/60 bg-emerald-500/[0.015]"
              : isExpired
                ? "border-destructive/20 bg-destructive/[0.015]"
                : "border-border"
          )}
        >
          {/* Accent bar */}
          <div
            className={cn(
              "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
              isConnected && !isExpired
                ? "bg-emerald-500"
                : isExpired
                  ? "bg-destructive"
                  : "bg-transparent"
            )}
          />

          <div className="p-5 pl-6">
            {/* Provider header row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3.5">
                <div
                  className={cn(
                    "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                    isConnected && !isExpired
                      ? "bg-emerald-500/10"
                      : isExpired
                        ? "bg-destructive/10"
                        : "bg-muted"
                  )}
                >
                  {provider === "google" ? (
                    <GoogleLogo className="w-5 h-5" />
                  ) : (
                    <MicrosoftLogo className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <p className="text-[15px] font-semibold leading-none">
                    {providerName} Account
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isConnected && connection ? connection.email : "Not connected"}
                  </p>
                </div>
              </div>

              {isConnected ? (
                isExpired ? (
                  <Badge variant="destructive" className="gap-1 text-xs">
                    <AlertTriangle className="h-3 w-3" />
                    Expired
                  </Badge>
                ) : (
                  <Badge className="gap-1 text-xs bg-emerald-500/10 text-emerald-700 border-emerald-200 hover:bg-emerald-500/10">
                    <Check className="h-3 w-3" />
                    Connected
                  </Badge>
                )
              ) : (
                <Badge variant="secondary" className="gap-1 text-xs">
                  <X className="h-3 w-3" />
                  Not connected
                </Badge>
              )}
            </div>

            {/* Connected state details */}
            {isConnected && connection && (
              <AnimatePresence>
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto" as const, opacity: 1, transition: { duration: 0.35, ease: EASE } }}
                  className="overflow-hidden"
                >
                  {/* Token expired warning */}
                  {isExpired && (
                    <div className="mt-4 p-3 rounded-lg border-l-2 border-l-destructive bg-destructive/5">
                      <p className="text-xs text-foreground/70 leading-relaxed">
                        <strong>Connection expired.</strong> Please reconnect your {providerName} account to continue syncing.
                      </p>
                    </div>
                  )}

                  {/* Connection details */}
                  <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>Connected</span>
                      <span className="text-foreground font-medium">
                        {new Date(connection.connectedAt).toLocaleDateString()}
                      </span>
                    </div>
                    {connection.lastUsedAt && (
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span>Last used</span>
                        <span className="text-foreground font-medium">
                          {new Date(connection.lastUsedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Capabilities */}
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail className="h-3.5 w-3.5 text-primary" />
                      Email sending
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5 text-primary" />
                      Calendar sync
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenCalendarSelect(provider)}
                      className="gap-2 flex-1"
                    >
                      <Calendar className="h-3.5 w-3.5" />
                      Select Calendar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(provider)}
                      disabled={disconnecting === provider}
                      className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/5 hover:border-destructive/30"
                    >
                      {disconnecting === provider ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Unplug className="h-3.5 w-3.5" />
                      )}
                      Disconnect
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {/* Not connected state */}
            {!isConnected && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={provider === "google" ? handleConnectGoogle : handleConnectMicrosoft}
                  disabled={connecting === provider}
                  className="w-full gap-2"
                >
                  {connecting === provider ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Connect {providerName}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // ── Sync status panel ───────────────────────────────────────
  const renderSyncStatusPanel = (
    providerKey: "google" | "microsoft",
    statusData: { connected: boolean; email?: string; lastSync?: string }
  ) => {
    const providerName = providerKey === "google" ? "Google" : "Microsoft";
    const isSynced = statusData.connected;

    return (
      <motion.div variants={fadeUp}>
        <div
          className={cn(
            "relative rounded-xl border transition-all duration-300",
            "hover:shadow-md hover:shadow-black/[0.03]",
            isSynced
              ? "border-primary/20 bg-primary/[0.015]"
              : "border-border"
          )}
        >
          <div
            className={cn(
              "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
              isSynced ? "bg-primary" : "bg-transparent"
            )}
          />

          <div className="flex items-center justify-between p-4 pl-5">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex items-center justify-center h-9 w-9 rounded-lg",
                  isSynced
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                )}
              >
                <Calendar className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">{providerName} Calendar</p>
                <p className="text-xs text-muted-foreground">{statusData.email}</p>
                {statusData.lastSync && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Last synced: {new Date(statusData.lastSync).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            <Badge
              className={cn(
                "gap-1 text-xs",
                isSynced
                  ? "bg-emerald-500/10 text-emerald-700 border-emerald-200 hover:bg-emerald-500/10"
                  : "bg-muted text-muted-foreground"
              )}
              variant={isSynced ? "outline" : "secondary"}
            >
              {isSynced ? (
                <><CheckCircle2 className="h-3 w-3" /> Synced</>
              ) : (
                <><XCircle className="h-3 w-3" /> Not Synced</>
              )}
            </Badge>
          </div>
        </div>
      </motion.div>
    );
  };

  const hasAnyConnection = googleConnection?.status === "connected" || microsoftConnection?.status === "connected";
  const hasCalendarSyncEnabled = calendarPreferences?.googleEnabled || calendarPreferences?.microsoftEnabled;

  // ── Loading skeleton ────────────────────────────────────────
  if (loading) {
    return (
      <Card className="overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-muted animate-pulse" />
            <div className="space-y-2">
              <div className="h-5 w-52 bg-muted rounded animate-pulse" />
              <div className="h-3.5 w-72 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
        </div>
        <CardContent className="pt-0 pb-6 space-y-3">
          <div className="h-24 rounded-xl bg-muted/50 animate-pulse" />
          <div className="h-24 rounded-xl bg-muted/50 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Main Connection Card ── */}
      <Card className="overflow-hidden">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="px-6 pt-6 pb-4"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Email & Calendar Connection</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Connect your Google or Microsoft account to send emails and sync appointments
              </p>
            </div>
          </div>

          <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
        </motion.div>

        <CardContent className="pt-0 pb-6">
          {/* Info block */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05, ease: EASE }}
            className="mb-5 p-3 rounded-lg border-l-2 border-l-primary bg-primary/5"
          >
            <p className="text-xs text-foreground/70 leading-relaxed">
              Connecting your account allows you to send emails from your own email address and automatically sync appointments to your calendar.
            </p>
          </motion.div>

          {/* Provider panels */}
          <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
            {renderProviderPanel(googleConnection, "google")}
            {renderProviderPanel(microsoftConnection, "microsoft")}

            {/* Primary Email Provider Selection */}
            {googleConnection?.status === "connected" && microsoftConnection?.status === "connected" && (
              <motion.div variants={fadeUp}>
                <div className="relative rounded-xl border border-primary/20 bg-primary/[0.015] transition-all duration-300 hover:shadow-md hover:shadow-black/[0.03]">
                  <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-primary" />

                  <div className="p-5 pl-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 text-primary">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[15px] font-semibold leading-none">Primary Email Provider</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Choose which account to use by default for sending emails
                        </p>
                      </div>
                    </div>

                    <Select
                      value={calendarPreferences?.primaryEmailProvider || "google"}
                      onValueChange={async (value: "google" | "microsoft") => {
                        setSaving(true);
                        try {
                          const result = await oauthConnectionService.updateCalendarPreferences({
                            primaryEmailProvider: value,
                          });
                          if (result.success && result.preferences) {
                            setCalendarPreferences(result.preferences);
                            toast({
                              title: "Primary Email Updated",
                              description: `${value === "google" ? "Gmail" : "Outlook"} will be used as your primary email provider`,
                            });
                          }
                        } catch (error) {
                          toast({
                            title: "Error",
                            description: "Failed to update primary email provider",
                            variant: "destructive",
                          });
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="google">
                          <div className="flex items-center gap-2">
                            <GoogleLogo className="w-4 h-4" />
                            Gmail ({googleConnection.email})
                          </div>
                        </SelectItem>
                        <SelectItem value="microsoft">
                          <div className="flex items-center gap-2">
                            <MicrosoftLogo className="w-4 h-4" />
                            Outlook ({microsoftConnection.email})
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </CardContent>
      </Card>

      {/* ── Sync Status Card ── */}
      {hasAnyConnection && hasCalendarSyncEnabled && (
        <Card className="overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="px-6 pt-6 pb-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold tracking-tight">Sync Status</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Current synchronization status for connected calendars
                  </p>
                </div>
              </div>

              {/* Sync Now button in header */}
              <Button
                onClick={handleSyncAll}
                disabled={syncing}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3.5 w-3.5" />
                    Sync Now
                  </>
                )}
              </Button>
            </div>

            <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
          </motion.div>

          <CardContent className="pt-0 pb-6">
            <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
              {calendarPreferences?.googleEnabled && calendarStatus?.google &&
                renderSyncStatusPanel("google", calendarStatus.google)
              }
              {calendarPreferences?.microsoftEnabled && calendarStatus?.microsoft &&
                renderSyncStatusPanel("microsoft", calendarStatus.microsoft)
              }
            </motion.div>
          </CardContent>
        </Card>
      )}

      {/* ── Sync Conflicts Card ── */}
      {conflicts.length > 0 && (
        <Card className="overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="px-6 pt-6 pb-4"
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-amber-100 text-amber-600 shadow-sm">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight">Sync Conflicts</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {conflicts.length} pending conflict{conflicts.length !== 1 ? "s" : ""} require your attention
                </p>
              </div>
            </div>

            <div className="mt-5 h-px bg-gradient-to-r from-amber-400/25 via-amber-400/10 to-transparent" />
          </motion.div>

          <CardContent className="pt-0 pb-6">
            <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
              {conflicts.map((conflict) => (
                <motion.div key={conflict.id} variants={fadeUp}>
                  <div className="relative rounded-xl border border-amber-200/60 transition-all duration-300 hover:shadow-md hover:shadow-black/[0.03]">
                    <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-amber-400" />

                    <div className="p-5 pl-6 space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-[15px] font-semibold">
                            Appointment Conflict ({conflict.provider === "google" ? "Google" : "Microsoft"})
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Detected on {new Date(conflict.detectedAt).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant="outline" className="gap-1 text-xs border-amber-200 text-amber-700 bg-amber-50">
                          <AlertCircle className="h-3 w-3" />
                          Unresolved
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 rounded-lg bg-muted/50 space-y-1.5">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">App Version</p>
                          <p className="text-sm">{JSON.stringify(conflict.appVersion, null, 2)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 space-y-1.5">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Calendar Version</p>
                          <p className="text-sm">{JSON.stringify(conflict.calendarVersion, null, 2)}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleResolveConflict(conflict.id, "app")}
                          disabled={resolving === conflict.id}
                          size="sm"
                          className="flex-1 gap-1.5"
                        >
                          {resolving === conflict.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Keep App Version
                        </Button>
                        <Button
                          onClick={() => handleResolveConflict(conflict.id, "calendar")}
                          disabled={resolving === conflict.id}
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1.5"
                        >
                          {resolving === conflict.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Calendar className="h-3.5 w-3.5" />
                          )}
                          Keep Calendar Version
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </CardContent>
        </Card>
      )}

      {/* ── Calendar Selection Dialog ── */}
      <Dialog open={showCalendarSelect !== null} onOpenChange={(open) => !open && setShowCalendarSelect(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Calendar for Sync</DialogTitle>
            <DialogDescription>
              Choose which {showCalendarSelect === "google" ? "Google" : "Microsoft"} calendar to sync your appointments with.
            </DialogDescription>
          </DialogHeader>

          {loadingCalendars ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : calendars.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No calendars found. Make sure you have calendar access enabled.
            </div>
          ) : (
            <RadioGroup
              value={selectedCalendarId || ""}
              onValueChange={setSelectedCalendarId}
              className="space-y-3"
            >
              {calendars.map((cal) => (
                <div
                  key={cal.id}
                  className={cn(
                    "flex items-center space-x-3 p-3 rounded-xl border transition-all duration-200 cursor-pointer",
                    "hover:shadow-md hover:shadow-black/[0.03]",
                    selectedCalendarId === cal.id
                      ? "border-primary/30 bg-primary/[0.02]"
                      : "border-border hover:border-primary/20"
                  )}
                  onClick={() => setSelectedCalendarId(cal.id)}
                >
                  <RadioGroupItem value={cal.id} id={cal.id} />
                  <Label htmlFor={cal.id} className="flex-1 cursor-pointer">
                    <div className="font-medium">{cal.name}</div>
                    {cal.primary && (
                      <Badge className="text-xs mt-1 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
                        Primary
                      </Badge>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCalendarSelect(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSelectCalendar}
              disabled={!selectedCalendarId || loadingCalendars}
            >
              Select Calendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
